<template>
  <div>
    <Header />
    <FlashMessage ref="flashMessage" />

   <div class="product-details-page">
  <LoadingIndicator v-if="loading" message="Loading product details..." />

  <!-- ✅ Prevent undefined errors -->
  <div v-else-if="product" class="main-content">

    <!-- 🔥 PRODUCT GALLERY -->
    <div class="product-gallery">
      
      <!-- Main Image -->
      <div class="main-image">
        <img
          :src="selectedImage || getFullImageUrl(product.thumbnail)"
          :alt="product.name"
        />
      </div>

      <!-- Thumbnails -->
      <div class="thumbnail-carousel">
        <img
          v-for="(image, index) in product.images"
          :key="index"
          :src="getFullImageUrl(image.image_path)"
          :alt="`Thumbnail ${index + 1}`"
          @click="selectedImage = getFullImageUrl(image.image_path)"
          :class="{
            active: selectedImage === getFullImageUrl(image.image_path),
          }"
        />
      </div>
    </div>

    <!-- 🔥 PRODUCT INFO -->
    <div class="product-info">
      <h1>{{ product.name }}</h1>

      <div class="price">
        <span class="original-price">Tsh: {{ product.price }}</span>
        <span class="selling-price">Tsh: {{ product.discount_price }}</span>
      </div>

      <p class="description">{{ product.description }}</p>

      <p class="availability">
        Availability: <span>{{ product.stock }} in stock</span>
      </p>

      <!-- ✅ Safe access -->
      <div class="brand-category">
        <p><strong>Brand:</strong> {{ product.brand?.name }}</p>
        <p><strong>Category:</strong> {{ product.category?.name }}</p>
      </div>

      <div class="quantity-selector">
        <label for="quantity">Quantity:</label>
        <input
          type="number"
          id="quantity"
          v-model="quantity"
          min="1"
          :max="product.stock"
        />
      </div>

      <button class="add-to-cart-btn" @click="addToCart(product.id)">
        Add to Cart
      </button>

      <!-- Optional rating (safe fallback) -->
      <div class="product-rating">
        <span
          v-for="star in 5"
          :key="star"
          :class="{ filled: star <= (product.rating || 0) }"
        >
          ⭐
        </span>
      </div>
    </div>
  </div>

  <!-- 🔥 RELATED PRODUCTS -->
  <div class="related-products">
    <h2 v-if="relatedProducts.length > 0">You Might Also Like</h2>

    <div class="products-grid">
      <div
        class="product-card"
        v-for="relatedProduct in relatedProducts"
        :key="relatedProduct.id"
      >
        <img
          :src="getFullImageUrl(relatedProduct.thumbnail)"
          :alt="relatedProduct.name"
        />
        <h3>{{ relatedProduct.name }}</h3>
        <p class="price">Tsh: {{ relatedProduct.price }}</p>

        <button class="add-to-cart-btn">
          Add to Cart
        </button>
      </div>
    </div>
  </div>
</div>
  </div>
</template>

<script setup>
import Header from "../components/Header.vue";
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import LoadingIndicator from "@/components/Loading_indicator.vue";
import {
  FetchProductDetails,
  API_BASE_URL,
  AddToCart,
} from "@/API-requests/Requests";
import FlashMessage from "@/components/FlashMessage.vue";

const route = useRoute();
const productId = ref(route.params.id);

const product = ref({});

const relatedProducts = ref([]);

const loading = ref(true);
const error = ref(null);
const flashMessage = ref(null);

const selectedImage = ref("");

const quantity = ref(1);

onMounted(async () => {
  try {
    const response = await FetchProductDetails(productId.value);

    //  Correct assignments
    product.value = response.products;
    relatedProducts.value = response.related_products;

    //  FIX: use thumbnail (NOT image)
    selectedImage.value = getFullImageUrl(product.value.thumbnail);

    flashMessage.value.showFlashMessage(
      "success",
      "Product details loaded successfully!"
    );

  } catch (err) {
    console.error("Error fetching product details:", err);
    error.value = "Failed to load product details. Please try again later";

    flashMessage.value.showFlashMessage("failure", error.value);

  } finally {
    loading.value = false;
  }
});

const getFullImageUrl = (imagePath) => {
  return `${API_BASE_URL}${imagePath}`;
};

const addToCart = async (product_id) => {
  try {
    const response = await AddToCart(product_id, quantity.value);
    selectedImage.value = getFullImageUrl(product.value.image);
    flashMessage.value.showFlashMessage("success", "Product added to cart!");
  } catch (err) {
    console.error("Error adding product to cart:", err);
    error.value = "Error adding product to cart.";
    flashMessage.value.showFlashMessage("failure", error.value);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.product-details-page {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.main-content {
  display: flex;
  gap: 40px;
  margin-bottom: 40px;
}

.product-gallery {
  flex: 1;
}

.main-image img {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 8px;
}

.thumbnail-carousel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.thumbnail-carousel img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid transparent;
}

.thumbnail-carousel img.active {
  border-color: #42b983;
}

.product-info {
  flex: 1;
}

.product-info h1 {
  font-size: 2rem;
  margin-bottom: 10px;
}

.price {
  margin: 10px 0;
}

.original-price {
  text-decoration: line-through;
  color: #999;
  margin-right: 10px;
}

.selling-price {
  font-size: 1.5rem;
  font-weight: bold;
  color: #42b983;
}

.description {
  font-size: 1rem;
  color: #666;
  margin: 20px 0;
}

.availability {
  font-size: 1rem;
  color: #333;
}

.availability span {
  color: #42b983;
  font-weight: bold;
}

/* Quantity Selector */
.quantity-selector {
  margin: 20px 0;
}

.quantity-selector label {
  font-size: 1rem;
  margin-right: 10px;
}

.quantity-selector input {
  padding: 5px;
  font-size: 1rem;
  width: 60px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.add-to-cart-btn {
  padding: 10px 20px;
  font-size: 1rem;
  background-color: #42b983;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.add-to-cart-btn:hover {
  background-color: #369f6e;
}

/* Product Rating */
.product-rating {
  margin: 20px 0;
}

.product-rating .filled {
  color: #ffc107;
}

.rating-text {
  font-size: 0.9rem;
  color: #666;
  margin-left: 10px;
}

.product-reviews {
  flex: 1;
}

.product-reviews h2 {
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.review {
  margin-bottom: 20px;
}

.review-text {
  font-size: 1rem;
  color: #333;
}

.review-author {
  font-size: 0.9rem;
  color: #666;
  margin-top: 5px;
}

.related-products {
  margin-top: 40px;
}

.related-products h2 {
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.product-card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.product-card img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
}

.product-card h3 {
  font-size: 1.2rem;
  margin: 10px 0;
}

.product-card .price {
  font-size: 1rem;
  color: #42b983;
  margin-bottom: 10px;
}

.product-card .add-to-cart-btn {
  padding: 10px 20px;
  font-size: 1rem;
  background-color: #42b983;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.product-card .add-to-cart-btn:hover {
  background-color: #369f6e;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }

  .thumbnail-carousel {
    flex-direction: row;
    justify-content: center;
  }
}
</style>
